import { agentRegistry } from '../../agents/core/AgentRegistry';
import { PersonaSystem } from '../../agents/personas/PersonaSystem';
import { SkillManager } from '../../agents/skills/SkillSet';
import kleur from 'kleur';

export async function runAgentCommands(args: string[]) {
  const action = args[0] || "help";
  
  switch (action) {
    case 'list':
      await listAgents(args.slice(1));
      break;
    case 'create':
      await createAgent(args.slice(1));
      break;
    case 'show':
    case 'info':
      await showAgent(args.slice(1));
      break;
    case 'exec':
    case 'execute':
      await executeTask(args.slice(1));
      break;
    case 'remove':
    case 'rm':
      await removeAgent(args.slice(1));
      break;
    case 'templates':
      await listTemplates();
      break;
    case 'personas':
      await listPersonas();
      break;
    case 'skills':
      await listSkills(args.slice(1));
      break;
    case 'stats':
      await showStats();
      break;
    case 'health':
      await healthCheck();
      break;
    case 'cleanup':
      await cleanupAgents(args.slice(1));
      break;
    case 'help':
    default:
      showHelp();
      break;
  }
}

async function listAgents(args: string[]) {
  const filter = args[0]; // status, type, or all
  let agents = agentRegistry.list();
  
  if (filter && filter !== 'all') {
    if (['idle', 'busy', 'error', 'terminated'].includes(filter)) {
      agents = agentRegistry.listByStatus(filter as any);
    } else {
      agents = agentRegistry.listByType(filter);
    }
  }
  
  if (agents.length === 0) {
    console.log(kleur.gray('No agents found'));
    return;
  }
  
  console.log(kleur.cyan(`\n📋 Agents (${agents.length})`));
  console.log(kleur.gray('────────────────────────────────────────'));
  
  for (const agent of agents) {
    const stats = agent.getStats();
    const status = agent.getStatus();
    const statusColor = getStatusColor(status);
    
    console.log(`${kleur.bold(agent.name)} ${kleur.gray(`(${agent.id.substring(0, 8)}...)`)}`);
    console.log(`  Type: ${kleur.yellow(agent.type)}`);
    console.log(`  Status: ${statusColor(status)}`);
    console.log(`  Tasks: ${kleur.green(stats.tasksCompleted.toString())} completed, ${kleur.red(stats.errors.toString())} errors`);
    console.log(`  Success Rate: ${kleur.cyan(stats.successRate.toFixed(1) + '%')}`);
    console.log(`  Uptime: ${kleur.gray(formatDuration(stats.uptime))}`);
    console.log('');
  }
}

async function createAgent(args: string[]) {
  if (args.length < 2) {
    console.log(kleur.red('Error: Please specify template and agent name'));
    console.log('Usage: metiscode agents create <template> <name> [options]');
    console.log('\nAvailable templates:');
    agentRegistry.listTemplates().forEach(t => {
      console.log(`  ${kleur.yellow(t.name)} - ${t.description}`);
    });
    return;
  }
  
  const [templateName, agentName] = args;
  
  try {
    const agent = await agentRegistry.createFromTemplate(templateName, {
      name: agentName
    });
    
    console.log(kleur.green(`✅ Created agent: ${agent.name}`));
    console.log(`   ID: ${kleur.gray(agent.id)}`);
    console.log(`   Type: ${kleur.yellow(agent.type)}`);
    console.log(`   Status: ${kleur.cyan(agent.getStatus())}`);
    
  } catch (error: any) {
    console.log(kleur.red(`❌ Failed to create agent: ${error.message}`));
  }
}

async function showAgent(args: string[]) {
  if (args.length === 0) {
    console.log(kleur.red('Error: Please specify agent name or ID'));
    return;
  }
  
  const identifier = args[0];
  let agent = agentRegistry.get(identifier);
  
  if (!agent) {
    agent = agentRegistry.getByName(identifier);
  }
  
  if (!agent) {
    console.log(kleur.red(`Agent not found: ${identifier}`));
    return;
  }
  
  const stats = agent.getStats();
  const config = agent.getConfig();
  const persona = agent.getPersona();
  const skills = agent.getSkills();
  const resourceUsage = agent.getResourceUsage();
  const currentTask = agent.getCurrentTask();
  
  console.log(kleur.cyan(`\n🤖 Agent: ${agent.name}`));
  console.log(kleur.gray('────────────────────────────────────────'));
  
  console.log(`ID: ${kleur.gray(agent.id)}`);
  console.log(`Type: ${kleur.yellow(agent.type)}`);
  console.log(`Status: ${getStatusColor(agent.getStatus())(agent.getStatus())}`);
  
  if (currentTask) {
    console.log(`Current Task: ${kleur.cyan(currentTask.task)}`);
  }
  
  console.log(`\n${kleur.bold('Persona:')}`);
  console.log(`  Name: ${kleur.yellow(persona.name)}`);
  console.log(`  Style: ${persona.communicationStyle.tone} / ${persona.communicationStyle.verbosity}`);
  console.log(`  Expertise: ${persona.expertise.map(e => `${e.domain}:${e.level}`).join(', ')}`);
  
  console.log(`\n${kleur.bold('Skills:')}`);
  const skillDetails = skills.getSkillDetails ? skills.getSkillDetails() : [];
  skillDetails.forEach(skill => {
    console.log(`  ${kleur.green(skill.name)} (${skill.level}) - ${skill.category}`);
  });
  
  console.log(`\n${kleur.bold('Performance:')}`);
  console.log(`  Tasks Completed: ${kleur.green(stats.tasksCompleted.toString())}`);
  console.log(`  Errors: ${kleur.red(stats.errors.toString())}`);
  console.log(`  Success Rate: ${kleur.cyan(stats.successRate.toFixed(1) + '%')}`);
  console.log(`  Avg Duration: ${kleur.gray(stats.averageTaskDuration.toFixed(0) + 'ms')}`);
  console.log(`  Uptime: ${kleur.gray(formatDuration(stats.uptime))}`);
  
  console.log(`\n${kleur.bold('Resources:')}`);
  console.log(`  Memory: ${kleur.gray(formatBytes(resourceUsage.memoryUsage))} / ${kleur.gray(formatBytes(resourceUsage.memoryLimit))}`);
  console.log(`  Load: ${kleur.cyan(resourceUsage.taskLoad.toString())}`);
}

async function executeTask(args: string[]) {
  if (args.length < 2) {
    console.log(kleur.red('Error: Please specify agent name and task'));
    console.log('Usage: metiscode agents exec <agent-name> "<task>" [params]');
    return;
  }
  
  const [agentIdentifier, task] = args;
  const params = args[2] ? JSON.parse(args[2]) : {};
  
  let agent = agentRegistry.get(agentIdentifier);
  if (!agent) {
    agent = agentRegistry.getByName(agentIdentifier);
  }
  
  if (!agent) {
    console.log(kleur.red(`Agent not found: ${agentIdentifier}`));
    return;
  }
  
  console.log(kleur.cyan(`🚀 Executing task with ${agent.name}...`));
  console.log(kleur.gray(`Task: ${task}`));
  
  try {
    const startTime = Date.now();
    const result = await agent.execute({
      id: `task-${Date.now()}`,
      task,
      params
    });
    
    const duration = Date.now() - startTime;
    
    if (result.success) {
      console.log(kleur.green(`✅ Task completed successfully (${duration}ms)`));
      console.log('Result:', result.result);
    } else {
      console.log(kleur.red(`❌ Task failed: ${result.error}`));
    }
    
    if (result.metadata) {
      console.log(kleur.gray(`Metadata: ${JSON.stringify(result.metadata, null, 2)}`));
    }
    
  } catch (error: any) {
    console.log(kleur.red(`❌ Execution error: ${error.message}`));
  }
}

async function removeAgent(args: string[]) {
  if (args.length === 0) {
    console.log(kleur.red('Error: Please specify agent name or ID'));
    return;
  }
  
  const identifier = args[0];
  let agent = agentRegistry.get(identifier);
  
  if (!agent) {
    agent = agentRegistry.getByName(identifier);
  }
  
  if (!agent) {
    console.log(kleur.red(`Agent not found: ${identifier}`));
    return;
  }
  
  const agentName = agent.name;
  const success = await agentRegistry.remove(agent.id);
  
  if (success) {
    console.log(kleur.green(`✅ Removed agent: ${agentName}`));
  } else {
    console.log(kleur.red(`❌ Failed to remove agent: ${agentName}`));
  }
}

async function listTemplates() {
  const templates = agentRegistry.listTemplates();
  
  console.log(kleur.cyan(`\n📋 Agent Templates (${templates.length})`));
  console.log(kleur.gray('────────────────────────────────────────'));
  
  for (const template of templates) {
    console.log(`${kleur.bold(template.name)} - ${template.description}`);
    console.log(`  Type: ${kleur.yellow(template.type)}`);
    console.log(`  Persona: ${kleur.cyan(template.persona)}`);
    console.log(`  Skills: ${kleur.gray(template.skills.join(', '))}`);
    console.log('');
  }
}

async function listPersonas() {
  const personas = PersonaSystem.getAllPersonas();
  
  console.log(kleur.cyan(`\n👤 Available Personas (${personas.length})`));
  console.log(kleur.gray('────────────────────────────────────────'));
  
  for (const persona of personas) {
    console.log(`${kleur.bold(persona.name)} - ${persona.description}`);
    console.log(`  Style: ${persona.communicationStyle.tone} / ${persona.communicationStyle.verbosity}`);
    console.log(`  Traits: ${kleur.gray(persona.traits.map(t => t.name).join(', '))}`);
    console.log(`  Expertise: ${kleur.yellow(persona.expertise.map(e => `${e.domain}:${e.level}`).join(', '))}`);
    console.log('');
  }
}

async function listSkills(args: string[]) {
  const category = args[0];
  const skills = category ? 
    SkillManager.getSkillsByCategory(category) :
    SkillManager.listSkills().map(name => SkillManager.getSkill(name)).filter(Boolean);
  
  console.log(kleur.cyan(`\n🎯 Available Skills${category ? ` (${category})` : ''} (${skills.length})`));
  console.log(kleur.gray('────────────────────────────────────────'));
  
  const categories = new Map<string, any[]>();
  for (const skill of skills) {
    if (skill) {
      if (!categories.has(skill.category)) {
        categories.set(skill.category, []);
      }
      categories.get(skill.category)!.push(skill);
    }
  }
  
  for (const [cat, catSkills] of categories) {
    console.log(kleur.bold(`${cat.toUpperCase()}:`));
    for (const skill of catSkills) {
      const levelColor = skill.level === 'expert' ? kleur.green : 
                        skill.level === 'advanced' ? kleur.yellow : kleur.gray;
      console.log(`  ${kleur.cyan(skill.name)} ${levelColor(`(${skill.level})`)} - ${skill.description}`);
      if (skill.prerequisites?.length) {
        console.log(`    Prerequisites: ${kleur.gray(skill.prerequisites.join(', '))}`);
      }
    }
    console.log('');
  }
}

async function showStats() {
  const stats = agentRegistry.getStats();
  const resourceUsage = agentRegistry.getResourceUsage();
  
  console.log(kleur.cyan('\n📊 Agent Registry Statistics'));
  console.log(kleur.gray('────────────────────────────────────────'));
  
  console.log(`Total Agents: ${kleur.bold(stats.totalAgents.toString())}`);
  console.log(`Active Agents: ${kleur.green(stats.activeAgents.toString())}`);
  console.log(`Busy Agents: ${kleur.yellow(stats.busyAgents.toString())}`);
  console.log(`Error Agents: ${kleur.red(stats.errorAgents.toString())}`);
  
  console.log(`\nTask Statistics:`);
  console.log(`  Total Tasks: ${kleur.bold(stats.totalTasks.toString())}`);
  console.log(`  Completed: ${kleur.green(stats.completedTasks.toString())}`);
  console.log(`  Failed: ${kleur.red(stats.failedTasks.toString())}`);
  console.log(`  Avg Duration: ${kleur.gray(stats.averageTaskDuration.toFixed(0) + 'ms')}`);
  
  console.log(`\nResource Usage:`);
  console.log(`  Memory: ${kleur.gray(formatBytes(resourceUsage.totalMemory))} / ${kleur.gray(formatBytes(resourceUsage.maxTotalMemory))} (${resourceUsage.memoryUtilization.toFixed(1)}%)`);
  console.log(`  Tasks: ${kleur.cyan(resourceUsage.activeTasks.toString())} / ${resourceUsage.maxConcurrentTasks} (${resourceUsage.taskUtilization.toFixed(1)}%)`);
  console.log(`  Agents: ${kleur.bold(resourceUsage.agentCount.toString())} / ${resourceUsage.maxAgents}`);
}

async function healthCheck() {
  const health = await agentRegistry.healthCheck();
  
  console.log(kleur.cyan('\n🏥 Agent Registry Health Check'));
  console.log(kleur.gray('────────────────────────────────────────'));
  
  const healthColor = health.healthy ? kleur.green : kleur.red;
  const healthIcon = health.healthy ? '✅' : '❌';
  
  console.log(`Status: ${healthIcon} ${healthColor(health.healthy ? 'Healthy' : 'Issues detected')}`);
  
  if (health.issues.length > 0) {
    console.log(`\nIssues:`);
    for (const issue of health.issues) {
      console.log(`  ${kleur.red('•')} ${issue}`);
    }
  }
  
  console.log(`\nQuick Stats:`);
  console.log(`  Active Agents: ${kleur.green(health.stats.activeAgents.toString())}`);
  console.log(`  Memory Usage: ${kleur.gray(health.resourceUsage.memoryUtilization.toFixed(1) + '%')}`);
  console.log(`  Task Load: ${kleur.cyan(health.resourceUsage.taskUtilization.toFixed(1) + '%')}`);
}

async function cleanupAgents(args: string[]) {
  const force = args.includes('--force');
  const removeIdle = args.includes('--idle') || force;
  const removeError = args.includes('--error') || force;
  
  console.log(kleur.cyan('🧹 Cleaning up agents...'));
  
  const removedCount = await agentRegistry.cleanup({
    removeIdleAgents: removeIdle,
    removeErrorAgents: removeError,
    maxIdleTime: 30 * 60 * 1000 // 30 minutes
  });
  
  console.log(kleur.green(`✅ Cleanup completed. Removed ${removedCount} agents.`));
  
  const stats = agentRegistry.getStats();
  console.log(kleur.gray(`Remaining agents: ${stats.totalAgents}`));
}

function showHelp() {
  console.log(kleur.cyan(`
🤖 Agent Management Commands

Usage: metiscode agents <action> [options]

Actions:
  list [filter]              List all agents or filter by status/type
  create <template> <name>   Create new agent from template
  show <name/id>             Show detailed agent information
  exec <name> "<task>"       Execute task with specific agent
  remove <name/id>           Remove agent
  
  templates                  List available agent templates
  personas                   List available personas  
  skills [category]          List available skills
  
  stats                      Show registry statistics
  health                     Run health check
  cleanup [--idle|--error]   Clean up idle/error agents

Examples:
  metiscode agents create developer mydev
  metiscode agents list busy
  metiscode agents exec mydev "implement user authentication"
  metiscode agents show mydev
  metiscode agents remove mydev

Filters for list:
  all, idle, busy, error, terminated (by status)
  developer, reviewer, devops (by type)
`));
}

// Utility functions
function getStatusColor(status: string) {
  switch (status) {
    case 'idle': return kleur.green;
    case 'busy': return kleur.yellow;
    case 'error': return kleur.red;
    case 'terminated': return kleur.gray;
    default: return kleur.gray;
  }
}

function formatDuration(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  
  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}